import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'

export default function Trusts() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [trusts, setTrusts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTrusts()
  }, [])

  const fetchTrusts = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('trust_members')
      .select('role, trusts(*)')
      .eq('user_id', user.id)

    if (!error && data) {
      console.log('Raw trust data:', JSON.stringify(data))
      const trustsWithRole = data.map(d => ({
        ...d.trusts,
        myRole: d.role
      }))
      setTrusts(trustsWithRole)
    }
    setLoading(false)
  }

  const statusColors = {
    active: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    closed: 'bg-gray-100 text-gray-500',
  }

  const roleColors = {
    owner: 'bg-blue-100 text-blue-700',
    admin: 'bg-purple-100 text-purple-700',
    trustee: 'bg-indigo-100 text-indigo-700',
    professional: 'bg-orange-100 text-orange-700',
    read_only: 'bg-gray-100 text-gray-600',
    report_only: 'bg-teal-100 text-teal-700',
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading trusts...</p>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">My Trusts</h2>
            <p className="text-sm text-gray-500 mt-1">
              {trusts.length} trust{trusts.length !== 1 ? 's' : ''} under management
            </p>
          </div>
          <button
            onClick={() => navigate('/trusts/new')}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition"
          >
            + New Trust
          </button>
        </div>

        {/* Empty State */}
        {trusts.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <p className="text-4xl mb-4">🏛</p>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No trusts yet</h3>
            <p className="text-sm text-gray-400 mb-6">
              Create your first trust to start tracking accounts, transactions, and reports.
            </p>
            <button
              onClick={() => navigate('/trusts/new')}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition"
            >
              Create First Trust
            </button>
          </div>
        )}

        {/* Trust Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {trusts.map((trust) => (
            <div
              key={trust.id}
              onClick={() => {
                console.log('Clicking trust:', trust.id, trust.name)
                navigate(`/trusts/${trust.id}`)
              }}
              className="bg-white rounded-xl border border-gray-200 p-5 cursor-pointer hover:shadow-md hover:border-blue-200 transition-all"
            >
              {/* Trust Name & Status */}
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-gray-900 text-base leading-tight pr-2">
                  {trust.name}
                </h3>
                <span className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${statusColors[trust.status] || statusColors.active}`}>
                  {trust.status}
                </span>
              </div>

              {/* Trust Details */}
              <div className="space-y-1.5 mb-4">
                {trust.state_of_domicile && (
                  <p className="text-xs text-gray-500">
                    📍 {trust.state_of_domicile}
                  </p>
                )}
                {trust.date_established && (
                  <p className="text-xs text-gray-500">
                    📅 Established {new Date(trust.date_established).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                )}
              </div>

              {/* My Role Badge */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${roleColors[trust.myRole] || roleColors.trustee}`}>
                  {trust.myRole?.replace('_', ' ')}
                </span>
                <span className="text-xs text-blue-500 font-medium">View →</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  )
}